import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Avatar,
  Button,
  Divider,
  Chip,
  Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  AccessTime,
  Edit,
  Delete,
} from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { AnyEvent, ExpertEvent } from './types';
import { CATEGORY_META, CATEGORY_LABELS } from './constants';
import { ModalIndices } from '../../common/i-doc/i-doc.registry.ts';
import { FoodEventDetails } from '@components/clients/calendar/events/food/FoodEventDetails';
import { WorkoutEventDetails } from '@components/clients/calendar/events/workout/WorkoutEventDetails';
import { MedicationEventDetails } from '@components/clients/calendar/events/medication/MedicationEventDetails';
import { FluidEventDetails } from '@components/clients/calendar/events/fluid/FluidEventDetails';
import { FeelingEventDetails } from '@components/clients/calendar/events/feeling/FeelingEventDetails';
import { TaskEventDetails } from '@components/clients/calendar/events/task/TaskEventDetails';
import { AppointmentEventDetails } from '@components/clients/calendar/events/appointment/AppointmentEventDetails';

interface EventDetailModalProps {
  event: AnyEvent | null;
  onClose: () => void;
  onEdit: (event: AnyEvent) => void;
  onDelete: (event: AnyEvent) => void;
}

export const EventDetailModal: React.FC<EventDetailModalProps> = ({
  event,
  onClose,
  onEdit,
  onDelete,
}) => {
  const [rawExpanded, setRawExpanded] = useState(false);

  if (!event) return null;

  const meta = CATEGORY_META[event.type];
  const label = CATEGORY_LABELS[event.type];
  const expertEvent = event.fromExpert ? (event as ExpertEvent) : null;
  const hasDetailsComponent = !!event.raw;

  return (
    <Dialog open={!!event} onClose={onClose} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }} data-i-doc={ModalIndices.EVENT_DETAIL}>
      <DialogContent sx={{ pt: 3, pb: 1 }}>

        {/* Тип події */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
          <Avatar sx={{ width: 44, height: 44, bgcolor: alpha(meta.color, 0.15), color: meta.color }}>
            {meta.icon}
          </Avatar>
          <Box>
            <Chip label={label} size="small" sx={{ bgcolor: alpha(meta.color, 0.12), color: meta.color, fontWeight: 600, fontSize: 11 }} />
            {event.completed && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                Виконано
              </Typography>
            )}
          </Box>
        </Box>

        {/* Назва */}
        <Typography variant="body1" sx={{ fontWeight: 600, mb: 1.5, lineHeight: 1.4 }}>
          {event.title}
        </Typography>

        {/* Час */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0 }}>
          <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
          <Typography variant="body2" color="text.secondary">
            {event.time}
          </Typography>
        </Box>

        {/* Деталі події */}
        {hasDetailsComponent && event.raw && (
          <>
            {event.type === 'food'        && <FoodEventDetails event={event.raw} />}
            {event.type === 'workout'     && <WorkoutEventDetails event={event.raw} />}
            {event.type === 'medication'  && <MedicationEventDetails event={event.raw} />}
            {event.type === 'liquid'      && <FluidEventDetails event={event.raw} />}
            {event.type === 'feeling'     && <FeelingEventDetails event={event.raw} />}
            {event.type === 'task'        && <TaskEventDetails event={event.raw} />}
            {event.type === 'appointment' && <AppointmentEventDetails event={event.raw} />}
          </>
        )}

        {/* Блок експерта */}
        {expertEvent && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Avatar sx={{
                width: 40, height: 40,
                bgcolor: expertEvent.expert.color,
                fontSize: 14, fontWeight: 700,
              }}>
                {expertEvent.expert.initials}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {expertEvent.expert.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Призначено експертом
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {/* Raw JSON */}
        {event.raw && (
          <>
            <Divider sx={{ my: 2 }} />
            <Box
              onClick={() => setRawExpanded(v => !v)}
              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer', userSelect: 'none' }}
            >
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Raw data
              </Typography>
              <ExpandMoreIcon
                sx={{
                  fontSize: 16,
                  color: 'text.secondary',
                  transition: 'transform 0.2s',
                  transform: rawExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                }}
              />
            </Box>
            <Collapse in={rawExpanded}>
              <Box
                component="pre"
                sx={{
                  mt: 0.75,
                  p: 1.5,
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  fontSize: '0.7rem',
                  lineHeight: 1.5,
                  overflowX: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  maxHeight: 300,
                  overflowY: 'auto',
                  fontFamily: 'monospace',
                  color: '#333',
                }}
              >
                {JSON.stringify(event.raw, null, 2)}
              </Box>
            </Collapse>
          </>
        )}

      </DialogContent>

      <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1 }}>
        {!expertEvent && (
          <>
            <Button
              variant="outlined"
              color="error"
              startIcon={<Delete />}
              onClick={() => onDelete(event)}
              sx={{ flex: 1 }}
            >
              Видалити
            </Button>
            <Button
              variant="contained"
              startIcon={<Edit />}
              onClick={() => onEdit(event)}
              sx={{ flex: 1 }}
            >
              Редагувати
            </Button>
          </>
        )}
        {expertEvent && (
          <Button variant="outlined" onClick={onClose} fullWidth>
            Закрити
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
